import React, { useState, useContext } from "react";
import { MessagesDialog, ProfileDialog } from "containers";
import { InfoDialog } from "components";
import ISXUtils, { ISXContext, APP_BAR_HEIGHT } from "services/Utils";
import { makeStyles } from "@mui/styles";
import clsx from "clsx";
import Grid from "@mui/material/Unstable_Grid2";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Button from "@mui/material/Button";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Link from "@mui/material/Link";
import AccountCircle from "@mui/icons-material/AccountCircle";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import MenuItem from "@mui/material/MenuItem";
import Menu from "@mui/material/Menu";
import Tooltip from "@mui/material/Tooltip";
import MailIcon from "@mui/icons-material/Mail";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import InfoIcon from "@mui/icons-material/InfoOutlined";
import PolicyIcon from "@mui/icons-material/Policy";
import SecurityIcon from "@mui/icons-material/Security";
import PersonIcon from "@mui/icons-material/Person";
import Badge from "@mui/material/Badge";
import { red } from "@mui/material/colors";

const useStyles = makeStyles((theme) => ({
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    width: "100%",
    height: APP_BAR_HEIGHT,
    backgroundColor: "#ffffff",
  },
  profileButton: {
    height: APP_BAR_HEIGHT,
    textTransform: "inherit",
    color: theme.palette.text.primary,
  },
}));

const NavigationBar = (props) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [infoDialogOpen, setInfoDialogOpen] = useState(false);
  const [messagesDialogOpen, setMessagesDialogOpen] = useState(false);
  const [profileDialogOpen, setProfileDialogOpen] = useState(false);
  const isxContext = useContext(ISXContext);

  const handleProfileMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const openInfoDialog = () => {
    setInfoDialogOpen(true);
    handleProfileMenuClose();
  };

  const openProfileDialog = () => {
    setProfileDialogOpen(true);
    handleProfileMenuClose();
  };

  const closeProfileDialog = () => {
    setProfileDialogOpen(false);
  };

  const closeInfoDialog = () => setInfoDialogOpen(false);

  const openMessagesDialog = () => {
    setMessagesDialogOpen(true);
    handleProfileMenuClose();
  };

  const closeMessagesDialog = () => {
    setMessagesDialogOpen(false);
  };

  const logout = () => {
    setAnchorEl(null);
    props.logout();
  };

  const showTermsOfUse = () => {
    window.open(isxContext.termsOfUse, "_blank");
    handleProfileMenuClose();
  };

  const showPrivacyPolicy = () => {
    window.open(isxContext.privacyPolicy, "_blank");
    handleProfileMenuClose();
  };

  const { isAuthenticated, username, user, messages, children } = props;

  const classes = useStyles();

  const numUserMessages = (messages && Object.keys(messages).length) || 0;

  return (
    <Box
      sx={{
        width: "100%",
        marginTop: APP_BAR_HEIGHT / 8,
      }}
    >
      <AppBar className={clsx(classes.appBar)}>
        <Toolbar style={{ overflow: "hidden" }}>
          <InfoDialog open={infoDialogOpen} onClose={closeInfoDialog} />
          <MessagesDialog
            open={messagesDialogOpen}
            handleClose={closeMessagesDialog}
          />
          {profileDialogOpen && (
            <ProfileDialog open={profileDialogOpen} close={closeProfileDialog} />
          )}
          <Link
            href={
              (isxContext.logo && isxContext.logo.link) ||
              "https://www.interstacks.com/"
            }
            underline="hover"
          >
            <img
              src={
                (isxContext.logo && isxContext.logo.url) ||
                "https://www.interstacks.com/wp-content/themes/boxpress/assets/img/branding/interstacks-logo.png"
              }
              height={(isxContext.logo && isxContext.logo.height) || 72}
              alt="Interstacks Logo"
            />
          </Link>
          {isxContext.logo && isxContext.logo.isxLink && (
            <Typography
              variant="subtitle1"
              style={{
                fontWeight: 400,
                marginLeft: "8px",
                marginRight: "8px",
                color: "#E28C05",
                alignSelf: "flex-end",
                fontSize: "0.75rem",
              }}
            >
              <Link
                href="https://www.interstacks.com/"
                color="inherit"
                underline="hover"
              >
                Powered by Interstacks
              </Link>
            </Typography>
          )}
          <Typography style={{ flex: 1 }} />
          {isAuthenticated && (
            <div>
              <Tooltip
                placement="bottom-end"
                title={
                  numUserMessages > 0
                    ? `You have ${numUserMessages} pending message${
                        numUserMessages > 1 ? "s" : ""
                      }`
                    : user.name || username
                }
              >
                <Button
                  sx={{
                    height: APP_BAR_HEIGHT,
                    textTransform: "inherit",
                    color: "text.primary",
                  }}
                  aria-owns={anchorEl ? "profile-menu" : null}
                  aria-haspopup="true"
                  onClick={handleProfileMenu}
                >
                  <Grid container direction="column" alignItems="flex-end">
                    <Grid>
                      <Typography variant="body2" component="p">
                        {user.name || username}
                      </Typography>
                    </Grid>
                    <Grid>
                      <Typography
                        variant="caption"
                        style={{ opacity: 0.66 }}
                        component="p"
                      >
                        {`${isxContext.labels.organization} ${
                          ISXUtils.isUserAdmin(user) ? "Admin" : "User"
                        }`}
                      </Typography>
                    </Grid>
                  </Grid>
                  <Badge
                    overlap="circular"
                    badgeContent={
                      numUserMessages > 0 ? (
                        <MailIcon
                          color="error"
                          style={{ height: "1rem", color: red[500] }}
                        />
                      ) : (
                        0
                      )
                    }
                  >
                    <AccountCircle style={{ marginLeft: 6, fill: "#999" }} />
                  </Badge>
                </Button>
              </Tooltip>
              <Menu
                id="profile-menu"
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
                onClose={handleProfileMenuClose}
              >
                {numUserMessages > 0 && (
                  <MenuItem onClick={openMessagesDialog}>
                    <ListItemIcon>
                      <MailOutlineIcon />
                    </ListItemIcon>
                    <ListItemText
                      primary={`${numUserMessages} Message${
                        numUserMessages > 1 ? "s" : ""
                      }`}
                    />
                  </MenuItem>
                )}
                <MenuItem onClick={openInfoDialog}>
                  <ListItemIcon>
                    <InfoIcon />
                  </ListItemIcon>
                  <ListItemText
                    primary={`About ${isxContext.labels.platform}`}
                  />
                </MenuItem>
                <MenuItem onClick={openProfileDialog}>
                  <ListItemIcon>
                    <PersonIcon />
                  </ListItemIcon>
                  <ListItemText primary={"My Profile"} />
                </MenuItem>
                {isxContext.termsOfUse && (
                  <MenuItem onClick={showTermsOfUse}>
                    <ListItemIcon>
                      <PolicyIcon />
                    </ListItemIcon>
                    <ListItemText primary="Terms of Use" />
                  </MenuItem>
                )}
                {isxContext.privacyPolicy && (
                  <MenuItem onClick={showPrivacyPolicy}>
                    <ListItemIcon>
                      <SecurityIcon />
                    </ListItemIcon>
                    <ListItemText primary="Privacy Policy" />
                  </MenuItem>
                )}
                <MenuItem onClick={logout}>
                  <ListItemIcon>
                    <PowerSettingsNewIcon />
                  </ListItemIcon>
                  <ListItemText primary="Logout" />
                </MenuItem>
              </Menu>
            </div>
          )}
        </Toolbar>
      </AppBar>
      {children}
    </Box>
  );
};

export default NavigationBar;
